import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { IpadFrame } from './components/snak/IpadFrame';
import { StudyDesignOverlay } from './components/snak/StudyDesignOverlay';
import { ClusterCompareOverlay } from './components/snak/ClusterCompareOverlay';
import { ClusterLightbox } from './components/snak/ClusterLightbox';
import { BriefOverlay } from './components/snak/BriefOverlay';
import { StoryboardOverlay } from './components/snak/StoryboardOverlay';
import { PermissionPage } from './components/snak/pages/PermissionPage';
import { ExpertQuestionPage } from './components/snak/pages/ExpertQuestionPage';
import { StudyBridgePage } from './components/snak/pages/StudyBridgePage';
import { ClusterRevealPage } from './components/snak/pages/ClusterRevealPage';
import { ReflectionPage } from './components/snak/pages/ReflectionPage';
import { EvidencePage } from './components/snak/pages/EvidencePage';
import { RelevancePage } from './components/snak/pages/RelevancePage';
import { ChooseDepthPage } from './components/snak/pages/ChooseDepthPage';
import { EndPage } from './components/snak/pages/EndPage';
import { chapters, chooseDepth, clusterReveal, evidence, expertQuestion, permission, reflection, relevance, repPrompts, snakReference, studyBridge } from './data/snak';
import type { Cluster } from './types/teaser';
import type { DepthChoice } from './types/snak';
const EASE = [0.23, 1, 0.32, 1] as const;
const PAGES = ['permission', 'question', 'bridge', 'reveal', 'reflection', 'evidence', 'relevance', 'depth', 'end'] as const;
type PageId = (typeof PAGES)[number];
interface AppProps {
  /** Show live NOK delegate responses after the reflection answer. */
  showPeerData?: boolean;
  /** Show the rep's verbal prompts on screen by default. */
  showRepPrompts?: boolean;
}
export function App({
  showPeerData = true,
  showRepPrompts = true
}: AppProps) {
  const [step, setStep] = useState(0);
  const [promptsVisible, setPromptsVisible] = useState(showRepPrompts);
  const [studyOpen, setStudyOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [briefOpen, setBriefOpen] = useState(false);
  const [storyboardOpen, setStoryboardOpen] = useState(false);
  const [zoomed, setZoomed] = useState<Cluster | null>(null);
  const [answers, setAnswers] = useState<Record<string, string | null>>({
    question: null,
    reflection: null,
    relevance: null
  });
  const page: PageId = PAGES[step];
  const goTo = (id: PageId) => setStep(PAGES.indexOf(id));
  const next = () => setStep(s => Math.min(s + 1, PAGES.length - 1));
  const back = () => setStep(s => Math.max(s - 1, 0));
  const answer = (key: string) => (id: string) => setAnswers(prev => ({
    ...prev,
    [key]: prev[key] === null ? id : prev[key]
  }));
  const restart = () => {
    setAnswers({
      question: null,
      reflection: null,
      relevance: null
    });
    setStep(0);
  };
  const onDepth = (choice: DepthChoice) => {
    if (choice === 'clusters') setCompareOpen(true);else if (choice === 'study') setStudyOpen(true);else goTo('end');
  };
  const nav = (() => {
    switch (page) {
      case 'permission':
        return {
          onNext: next,
          nextLabel: permission.cta
        };
      case 'question':
        return {
          onBack: back,
          onNext: next,
          nextLabel: expertQuestion.cta,
          nextDisabled: answers.question === null,
          secondary: {
            label: 'Skip',
            onClick: next
          }
        };
      case 'bridge':
        return {
          onBack: back,
          onNext: next,
          nextLabel: studyBridge.cta,
          footnote: snakReference
        };
      case 'reveal':
        return {
          onBack: back,
          onNext: next,
          nextLabel: clusterReveal.cta,
          footnote: snakReference
        };
      case 'reflection':
        return {
          onBack: back,
          onNext: next,
          nextLabel: reflection.cta,
          nextDisabled: answers.reflection === null,
          secondary: {
            label: 'Skip',
            onClick: next
          }
        };
      case 'evidence':
        return {
          onBack: back,
          onNext: next,
          nextLabel: evidence.cta,
          footnote: snakReference
        };
      case 'relevance':
        return {
          onBack: back,
          onNext: next,
          nextLabel: relevance.cta,
          nextDisabled: answers.relevance === null,
          secondary: {
            label: 'Skip',
            onClick: next
          }
        };
      case 'depth':
        return {
          onBack: back,
          footnote: chooseDepth.support
        };
      case 'end':
        return {
          onBack: back,
          footnote: snakReference
        };
      default:
        return {
          onBack: back,
          onNext: next
        };
    }
  })();
  return <div className="min-h-full w-full bg-mist font-sans" data-id="element-0" data-mp="zxttkqw5okmb">
      <div className="print-hidden" data-id="element-1" data-mp="zw91r9187fs5">
      <IpadFrame step={step} total={PAGES.length} chapter={chapters[page]} prompt={repPrompts[page]} promptsVisible={promptsVisible} onTogglePrompts={() => setPromptsVisible(v => !v)} onOpenStudy={() => setStudyOpen(true)} onOpenBrief={() => setBriefOpen(true)} onOpenStoryboard={() => setStoryboardOpen(true)} {...nav} data-id="element-2" data-mp="vw8nboe4n4l3">
        <AnimatePresence mode="wait" data-id="element-3" data-mp="205qv8zntw8yf">
          <motion.div key={page} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.22,
            ease: EASE
          }} className="flex flex-1 flex-col" data-id="element-4" data-mp="206tkz49f05cr">
            {page === 'permission' && <PermissionPage data-id="element-5" data-mp="2eu4yhbp5pzfi" />}
            {page === 'question' && <ExpertQuestionPage selected={answers.question} onSelect={answer('question')} data-id="element-6" data-mp="2jt82qtxbc1mf" />}
            {page === 'bridge' && <StudyBridgePage data-id="element-7" data-mp="2nw4ec0ofqxgv" />}
            {page === 'reveal' && <ClusterRevealPage onEnlarge={setZoomed} data-id="element-8" data-mp="2sufn082zv1xi" />}
            {page === 'reflection' && <ReflectionPage selected={answers.reflection} onSelect={answer('reflection')} showPeerData={showPeerData} data-id="element-9" data-mp="thtpvtbr65bm" />}
            {page === 'evidence' && <EvidencePage onOpenStudy={() => setStudyOpen(true)} data-id="element-10" data-mp="21qsm6iyjyglh" />}
            {page === 'relevance' && <RelevancePage selected={answers.relevance} onSelect={answer('relevance')} data-id="element-11" data-mp="1wtixj5izm8yp" />}
            {page === 'depth' && <ChooseDepthPage onChoose={onDepth} data-id="element-12" data-mp="2atjy0jvcbedn" />}
            {page === 'end' && <EndPage onRestart={restart} data-id="element-13" data-mp="25s4b0m9lqxsm" />}
          </motion.div>
        </AnimatePresence>
      </IpadFrame>
      </div>

      <div className="print-hidden" data-id="element-14" data-mp="2kovf38u80xok">
        <AnimatePresence data-id="element-15" data-mp="2frd297d31yba">
          {zoomed && <ClusterLightbox cluster={zoomed} onClose={() => setZoomed(null)} data-id="element-16" data-mp="2fqaf9aivsg0n" />}
        </AnimatePresence>
        <AnimatePresence data-id="element-17" data-mp="2thgj1it70ztc">
          {compareOpen && <ClusterCompareOverlay onClose={() => setCompareOpen(false)} data-id="element-18" data-mp="2tkktgxx94rsg" />}
        </AnimatePresence>
        <AnimatePresence data-id="element-19" data-mp="2pjiv0jb5sjrj">
          {studyOpen && <StudyDesignOverlay onClose={() => setStudyOpen(false)} data-id="element-20" data-mp="33g4rbpvdm0ju" />}
        </AnimatePresence>
        <AnimatePresence data-id="element-21" data-mp="2yimcxjylat4g">
          {briefOpen && <BriefOverlay onClose={() => setBriefOpen(false)} data-id="element-22" data-mp="2yjo20scfxg56" />}
        </AnimatePresence>
      </div>

      <AnimatePresence data-id="element-23" data-mp="3i4efx1t6b4gp">
        {storyboardOpen && <StoryboardOverlay onClose={() => setStoryboardOpen(false)} data-id="element-24" data-mp="3n2g39yxmtpxz" />}
      </AnimatePresence>
    </div>;
}